import { useEffect, useRef } from 'react';

type UseShortPollingParams = {
  callback: () => void | Promise<void>;
  enabled?: boolean;
  executeImmediately?: boolean;
  interval: number;
};

const useShortPolling = ({ callback, enabled = true, executeImmediately = true, interval }: UseShortPollingParams) => {
  const callbackRef = useRef(callback);

  useEffect(() => {
    callbackRef.current = callback;
  }, [callback]);

  useEffect(() => {
    if (!enabled) {
      return undefined;
    }

    // Background tabs skip their ticks and catch up once when shown again.
    let missedWhileHidden = false;
    const runCallback = () => callbackRef.current();
    const tick = () => {
      if (document.visibilityState === 'hidden') {
        missedWhileHidden = true;
        return;
      }
      runCallback();
    };
    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible' && missedWhileHidden) {
        missedWhileHidden = false;
        runCallback();
      }
    };

    if (executeImmediately) {
      runCallback();
    }

    const intervalId = window.setInterval(tick, interval);
    document.addEventListener('visibilitychange', onVisibilityChange);

    return () => {
      window.clearInterval(intervalId);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [enabled, executeImmediately, interval]);
};

export default useShortPolling;
